import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clapperboard, Download, ImageIcon, RefreshCw, Scissors, Video, Wand2 } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import {
  ResponsiveAppShell,
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCard,
  TaskyDialog,
  TaskyInput,
  TaskyPageContainer,
  TaskySkeleton,
  TaskyTextarea,
  TaskyTopBar,
} from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { videoEditApi } from '../../components/video-edit/videoEditApi';
import type { VideoEdit } from '../../components/video-edit/types';
import recipeVideoService, {
  RecipeVideoDetail,
  RecipeVideoLineInput,
  RecipeVideoScene,
  isRecipeVideoBusy,
  parseRecipeVideoId,
} from '../../services/api/recipeVideoService';
import { useRecipeVideoStrings } from './strings';

/**
 * 動画スタイルレシピの詳細: 進捗 → 完成動画の再生・ダウンロード・本格編集 → シーンごとの編集 → 反映して作り直す。
 *
 * 作成中は詳細 API だけを POLL_MS ごとにポーリングする（タブが見えていないときは止める）。
 * 編集は台本に印を付けるだけで、「変更を反映して作り直す」を押すと変わった素材だけ作り直される。
 */

const POLL_MS = 4000;

type Strings = ReturnType<typeof useRecipeVideoStrings>;

interface SceneCardProps {
  scene: RecipeVideoScene;
  editable: boolean;
  t: Strings;
  onSaveLines: (index: number, lines: RecipeVideoLineInput[]) => Promise<void>;
  onRegenerateImage: (index: number, instruction: string) => Promise<void>;
  onRegenerateClip: (index: number) => Promise<void>;
}

const SceneCard: React.FC<SceneCardProps> = ({ scene, editable, t, onSaveLines, onRegenerateImage, onRegenerateClip }) => {
  const [lines, setLines] = useState<RecipeVideoLineInput[]>(() =>
    scene.lines.map((l) => ({ text: l.text, caption_text: l.caption_text ?? '' })),
  );
  const [instruction, setInstruction] = useState(scene.image_instruction ?? '');
  const [busy, setBusy] = useState<'lines' | 'image' | 'clip' | null>(null);

  const changed = scene.lines.some(
    (l, j) => (lines[j]?.text ?? '') !== l.text || (lines[j]?.caption_text ?? '') !== (l.caption_text ?? ''),
  );

  const run = async (kind: 'lines' | 'image' | 'clip', action: () => Promise<void>) => {
    setBusy(kind);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <TaskyCard variant="bordered" padding="md" data-testid={`recipe-video-scene-${scene.index}`}>
      <div className="flex gap-4">
        <div className="w-24 shrink-0">
          <div className="flex aspect-[9/16] w-full items-center justify-center overflow-hidden rounded bg-gray-100">
            {scene.image_url ? (
              <img src={scene.image_url} alt={t.sceneLabel(scene.index)} className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-6 w-6 text-gray-300" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="text-sm font-semibold text-dark">{t.sceneLabel(scene.index)}</span>
            <TaskyBadge variant="secondary">{t.kind[scene.kind] ?? scene.kind}</TaskyBadge>
            {scene.start_seconds !== null && scene.duration_seconds !== null && (
              <span>
                {t.seconds(scene.start_seconds)} / {t.seconds(scene.duration_seconds)}
              </span>
            )}
          </div>

          {lines.map((line, j) => (
            <div key={j} className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600" htmlFor={`scene-${scene.index}-line-${j}`}>
                {t.line}
              </label>
              <TaskyTextarea
                id={`scene-${scene.index}-line-${j}`}
                value={line.text}
                rows={2}
                maxLength={120}
                // 保存中は止める（保存の応答で欄を作り直すので、その間に打った文字が消えないように）
                disabled={!editable || busy !== null}
                onChange={(e) => setLines((prev) => prev.map((l, k) => (k === j ? { ...l, text: e.target.value } : l)))}
              />
              <label className="block text-xs font-medium text-gray-600" htmlFor={`scene-${scene.index}-caption-${j}`}>
                {t.caption}
              </label>
              <TaskyInput
                id={`scene-${scene.index}-caption-${j}`}
                value={line.caption_text ?? ''}
                maxLength={120}
                placeholder={t.captionPlaceholder}
                disabled={!editable || busy !== null}
                onChange={(e) => setLines((prev) => prev.map((l, k) => (k === j ? { ...l, caption_text: e.target.value } : l)))}
              />
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            {lines.length > 0 && (
              <TaskyButton
                type="button"
                size="sm"
                variant="outline"
                disabled={!editable || !changed || lines.some((l) => l.text.trim() === '')}
                isLoading={busy === 'lines'}
                onClick={() =>
                  run('lines', () =>
                    onSaveLines(
                      scene.index,
                      lines.map((l) => ({ text: l.text.trim(), caption_text: l.caption_text?.trim() || null })),
                    ),
                  )
                }
              >
                {t.saveLines}
              </TaskyButton>
            )}
            {scene.can_regenerate_clip && (
              <TaskyButton
                type="button"
                size="sm"
                variant="outline"
                disabled={!editable}
                isLoading={busy === 'clip'}
                onClick={() => run('clip', () => onRegenerateClip(scene.index))}
              >
                <Video className="h-3.5 w-3.5" />
                {t.regenerateClip}
              </TaskyButton>
            )}
          </div>

          {scene.can_regenerate_image && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[12rem] flex-1">
                <TaskyInput
                  aria-label={t.instructionPlaceholder}
                  value={instruction}
                  maxLength={200}
                  placeholder={t.instructionPlaceholder}
                  disabled={!editable || busy !== null}
                  onChange={(e) => setInstruction(e.target.value)}
                />
              </div>
              <TaskyButton
                type="button"
                size="sm"
                variant="outline"
                disabled={!editable}
                isLoading={busy === 'image'}
                onClick={() => run('image', () => onRegenerateImage(scene.index, instruction))}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t.regenerateImage}
              </TaskyButton>
            </div>
          )}
        </div>
      </div>
    </TaskyCard>
  );
};

const RecipeVideoEditorPage: React.FC = () => {
  const t = useRecipeVideoStrings();
  const { projectId: rawProjectId } = useParams<{ projectId: string }>();
  // URL の ID は正の整数だけを受け付ける（それ以外は API を呼ばずに一覧へ戻す）
  const projectId = parseRecipeVideoId(rawProjectId);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [project, setProject] = useState<RecipeVideoDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // 本格編集を開いている最中（二重に押しても1回だけ作る。state は次の描画まで変わらないので ref で止める）
  const [openingEdit, setOpeningEdit] = useState(false);
  const openingEditRef = useRef(false);
  // 前の本格編集が、その後に動画レシピで作り直した動画より古いとき、作り直すか前の編集を開くかを選ばせる
  const [staleEdit, setStaleEdit] = useState<VideoEdit | null>(null);
  const mounted = useRef(true);
  // 応答の世代。編集・反映で状態を変えたら進め、それより前に投げた GET の応答は捨てる
  // （遅れて届いた古い「完成」で、反映後の作成中の表示とポーリングが止まらないように）
  const generation = useRef(0);
  const loading = useRef(false);

  useEffect(() => {
    if (projectId === null) navigate('/ads/recipe-video', { replace: true });
  }, [projectId, navigate]);

  const load = useCallback(async () => {
    if (projectId === null || loading.current) return;
    loading.current = true;
    const startedAt = generation.current;
    try {
      const data = await recipeVideoService.getProject(projectId);
      if (mounted.current && startedAt === generation.current) setProject(data);
    } catch (e) {
      if (mounted.current && startedAt === generation.current) setError(recipeVideoService.extractErrorMessage(e));
    } finally {
      loading.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  /** 編集・反映の応答で状態を置き換える（それより前の GET の応答は捨てる） */
  const replaceProject = (data: RecipeVideoDetail) => {
    generation.current += 1;
    setProject(data);
  };

  // 作成中だけ詳細をポーリングする（見えていないタブでは取りに行かない）
  const busy = project !== null && isRecipeVideoBusy(project.status);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  const handleError = (e: unknown) =>
    setError(recipeVideoService.isCreditInsufficientError(e) ? t.creditShort : recipeVideoService.extractErrorMessage(e));

  const edit = async (action: () => Promise<RecipeVideoDetail>, message: string) => {
    setError(null);
    try {
      replaceProject(await action());
      showToast(message);
    } catch (e) {
      handleError(e);
    }
  };

  const handleApply = async () => {
    if (!project || applying) return;
    setApplying(true);
    setError(null);
    try {
      replaceProject(await recipeVideoService.apply(project.id));
      showToast(t.applied);
    } catch (e) {
      handleError(e);
    } finally {
      setApplying(false);
    }
  };

  const openEdit = (edit: VideoEdit) => navigate(`/ads/recipe-video/edit/${edit.id}`);

  /** 完成動画を本格編集で開く（前の編集があればそれを開く。fresh=true なら最新の動画から作り直す） */
  const handleOpenFullEdit = async (fresh = false) => {
    if (!project || openingEditRef.current) return;
    openingEditRef.current = true;
    setOpeningEdit(true);
    try {
      const edit = await videoEditApi.fromRecipeVideo(project.id, fresh);
      if (edit.source_stale) setStaleEdit(edit);
      else openEdit(edit);
    } catch {
      showToast(t.fullEdit.failed, 'error');
    } finally {
      openingEditRef.current = false;
      if (mounted.current) setOpeningEdit(false);
    }
  };

  const pending = project?.pending_changes ?? null;

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={t.pageTitle}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title={project?.title ?? t.pageTitle}
          icon={<Clapperboard className="h-5 w-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            <TaskyButton type="button" variant="ghost" size="sm" onClick={() => navigate('/ads/recipe-video')} className="mb-4">
              <ArrowLeft />
              {t.back}
            </TaskyButton>

            {error && (
              <TaskyAlert tone="error" className="mb-4">
                {error}
              </TaskyAlert>
            )}

            {project === null ? (
              <TaskySkeleton className="h-96 w-full" />
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
                {/* 動画・進捗 */}
                <div className="space-y-4">
                  <TaskyCard variant="bordered" padding="md">
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <TaskyBadge
                        variant={project.status === 'completed' ? 'success' : project.status === 'failed' ? 'destructive' : 'info'}
                      >
                        {t.status[project.status]}
                      </TaskyBadge>
                      {project.style_name && <span>{project.style_name}</span>}
                      {project.appeal && <span>・{project.appeal}</span>}
                    </div>

                    {busy && (
                      <div className="mb-3" data-testid="recipe-video-progress">
                        <div className="mb-1 flex justify-between text-sm">
                          <span className="text-dark">{t.step[project.step]}</span>
                          <span className="font-semibold text-primary">{project.progress}%</span>
                        </div>
                        <div
                          className="h-2 overflow-hidden rounded-full bg-gray-200"
                          role="progressbar"
                          aria-valuenow={project.progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div className="h-full bg-primary transition-all" style={{ width: `${project.progress}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">{t.progressNote}</p>
                      </div>
                    )}

                    {project.status === 'failed' && (
                      <TaskyAlert tone="error" className="mb-3">
                        <div className="font-semibold">{t.failedTitle}</div>
                        {project.error && <div className="mt-1 text-xs">{project.error}</div>}
                        <TaskyButton type="button" size="sm" className="mt-2" onClick={handleApply} isLoading={applying}>
                          <RefreshCw className="h-3.5 w-3.5" />
                          {t.retry}
                        </TaskyButton>
                      </TaskyAlert>
                    )}

                    {project.video_url ? (
                      <div className="space-y-2">
                        <video
                          key={project.video_url}
                          src={project.video_url}
                          controls
                          playsInline
                          className="aspect-[9/16] w-full rounded bg-black"
                          data-testid="recipe-video-player"
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          {project.download_url && (
                            <a
                              href={project.download_url}
                              download
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                            >
                              <Download className="h-4 w-4" />
                              {t.download}
                              {project.output_seconds !== null && ` (${t.seconds(project.output_seconds)})`}
                            </a>
                          )}
                          {project.status === 'completed' && (
                            <TaskyButton
                              type="button"
                              size="sm"
                              variant="outline"
                              isLoading={openingEdit}
                              onClick={() => void handleOpenFullEdit()}
                            >
                              {!openingEdit && <Scissors className="h-3.5 w-3.5" />}
                              {openingEdit ? t.fullEdit.opening : t.fullEdit.open}
                            </TaskyButton>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex aspect-[9/16] w-full items-center justify-center rounded bg-gray-100 text-xs text-gray-400">
                        {t.noVideoYet}
                      </div>
                    )}
                  </TaskyCard>
                </div>

                {/* シーンの編集 */}
                <div className="space-y-4">
                  {pending?.dirty && (
                    <TaskyAlert tone="info" data-testid="recipe-video-pending">
                      <div className="font-semibold">{t.pendingTitle}</div>
                      <div className="mt-1 text-xs">
                        {t.pendingDetail(pending.images, pending.voices, pending.clips, pending.estimated_credits)}
                      </div>
                      <TaskyButton type="button" size="sm" className="mt-2" onClick={handleApply} isLoading={applying}>
                        <Wand2 className="h-3.5 w-3.5" />
                        {t.apply}
                      </TaskyButton>
                    </TaskyAlert>
                  )}

                  {!project.editable && project.scenes.length > 0 && (
                    <p className="text-xs text-gray-500">{t.busyEdit}</p>
                  )}

                  {project.scenes.length > 0 && <h2 className="text-sm font-semibold text-dark">{t.scenesTitle}</h2>}
                  {project.scenes.map((scene) => (
                    <SceneCard
                      // サーバーのセリフ・字幕が変わったとき（保存・反映の後）だけ作り直して入力を合わせる。
                      // effect で合わせると、初回の effect が入力より後に走って打った文字を消すことがある
                      key={`${scene.index}:${JSON.stringify(scene.lines)}:${scene.image_instruction ?? ''}`}
                      scene={scene}
                      editable={project.editable}
                      t={t}
                      onSaveLines={(index, lines) => edit(() => recipeVideoService.updateScene(project.id, index, lines), t.saved)}
                      onRegenerateImage={(index, instruction) =>
                        edit(() => recipeVideoService.regenerateImage(project.id, index, instruction), t.marked)
                      }
                      onRegenerateClip={(index) => edit(() => recipeVideoService.regenerateClip(project.id, index), t.marked)}
                    />
                  ))}
                </div>
              </div>
            )}
          </TaskyPageContainer>
        </div>
      </div>

      <TaskyDialog
        open={staleEdit !== null}
        onClose={() => setStaleEdit(null)}
        title={t.fullEdit.staleTitle}
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <TaskyButton type="button" variant="outline" onClick={() => staleEdit && openEdit(staleEdit)}>
              {t.fullEdit.keep}
            </TaskyButton>
            <TaskyButton
              type="button"
              onClick={() => {
                setStaleEdit(null);
                void handleOpenFullEdit(true);
              }}
            >
              {t.fullEdit.rebuild}
            </TaskyButton>
          </div>
        }
      >
        <p>{t.fullEdit.staleBody}</p>
      </TaskyDialog>
    </ResponsiveAppShell>
  );
};

export default RecipeVideoEditorPage;
